import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Alert,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import { infoStyle } from "services/constants";
import { themes } from "services/constants";
import { stickyHeader } from "services/constants";
import { CustomizedDatePicker } from "components/CustomComponents/CustomizedDatePicker";
import { useForm } from "react-hook-form";
import { atleastOneCharacterWithSpace } from "services/constants/patterns";
import { minimum3Characters } from "services/constants/ErrorMessages";
import { CustomTextBox } from "components/CustomComponents";
import { useSalaryRevise } from "./SalaryReviseContext";
import { showToast } from "services/utils/Status";
import { useSelector } from "react-redux";
import { customGetAPI } from "services/utils/CustomAPI";
import dayjs from "dayjs";

const TableSection = ({ title, data, totalLabel }) => {
  const total = useMemo(
    () => (data ?? []).reduce((sum, i) => sum + Number(i?.amount || 0), 0),
    [data],
  );

  return (
    <Box mt={3}>
      <Typography fontWeight={600} sx={{ ml: 1 }}>
        {title}
      </Typography>
      <TableContainer sx={{ mt: 2, backgroundColor: themes?.whiteColor }}>
        <Table size="small" className="border">
          <TableHead sx={stickyHeader}>
            <TableRow>
              <TableCell>Component</TableCell>
              <TableCell>Method</TableCell>
              <TableCell>Computation</TableCell>
              <TableCell sx={{ textAlign: "right" }}>Amount</TableCell>
            </TableRow>
          </TableHead>
          {!data || data.length === 0 ? (
            <TableBody>
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Alert severity="info">{"No data available"}</Alert>
                </TableCell>
              </TableRow>
            </TableBody>
          ) : (
            <TableBody>
              {data?.map((row, i) => (
                <TableRow key={i}>
                  <TableCell>{row?.component_name}</TableCell>
                  <TableCell>{row?.computation_method}</TableCell>
                  <TableCell>{row?.computation_value}</TableCell>
                  <TableCell sx={{ textAlign: "right" }}>
                    {" "}
                    ₹{row.amount}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell>
                  <strong>{totalLabel}</strong>
                </TableCell>
                <TableCell colSpan={2} />
                <TableCell align="right">
                  <strong>₹{total ?? 0}</strong>
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
      </TableContainer>
    </Box>
  );
};

const SummaryColumn = ({ title, summary }) => (
  <Box
    sx={{
      flex: 1,
      borderRadius: 2,
      p: 2,
      border:
        title === "Current Summary"
          ? `1px solid ${themes?.redColor}`
          : `1px solid ${themes?.greenColor}`,
      bgcolor: title === "Current Summary" && "#F3F2F7",
    }}
  >
    <Typography fontWeight={600}>{title}</Typography>

    {summary.map((item, i) => (
      <Box
        key={i}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          py: 2,
          borderBottom:
            i === summary?.length - 1 ? "none" : "2px solid #f0f0f0",
        }}
      >
        <Typography fontWeight={600}>{item.label}</Typography>
        <Typography fontWeight={600}>₹{item.value}</Typography>
      </Box>
    ))}
  </Box>
);

export const Preview = ({ currentTemplateData, revisionBasedOn, userId }) => {
  const [applicableFor, setApplicableFor] = useState("Non Override Employee");
  const {
    control,
    watch,
    register,
    setValue,
    trigger,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      start_date: null,
      reason: "",
    },
  });
  const {
    templateData,
    setPreviewData,
    previewData,
    triggerOnce,
    setTriggerOnce,
    getDate,
    setGetDate,
  } = useSalaryRevise();
  const { domain } = useSelector((state) => state.tokenReducer);
  const [getLastPayDate, setGetLastPayDate] = useState(null);
  const [onboardDate, setOnboardDate] = useState(null);

  const currentSummary = [
    { label: "Gross Salary", value: currentTemplateData?.total_salary ?? 0 },
    { label: "Deduction", value: currentTemplateData?.total_deductions ?? 0 },
    { label: "Net Salary", value: currentTemplateData?.net_salary ?? 0 },
    {
      label: "Employer Contribution",
      value: currentTemplateData?.total_employer_contribution ?? 0,
    },
    {
      label: "CTC",
      value: currentTemplateData?.total_ctc ?? 0,
    },
  ];

  const newSummary = [
    { label: "Gross Salary", value: templateData?.total_salary ?? 0 },
    { label: "Deduction", value: templateData?.total_deductions ?? 0 },
    { label: "Net Salary", value: templateData?.net_salary ?? 0 },
    {
      label: "Employer Contribution",
      value: templateData?.total_employer_contribution ?? 0,
    },
    { label: "CTC", value: templateData?.total_ctc ?? 0 },
  ];

  const startDate = watch("start_date");
  const reason = watch("reason");

  useEffect(() => {
    setPreviewData((prev) => ({
      ...prev,
      start_date: startDate,
      applicable_for: applicableFor,
      reason: reason,
      validate: async () => {
        return await trigger();
      },
    }));
  }, [
    startDate,
    reason,
    applicableFor,
    setPreviewData,
    trigger,
    previewData?.start_date,
    previewData?.reason,
  ]);

  const updateReason = (event) => {
    setValue("reason", event.target.value);
  };

  useEffect(() => {
    if (triggerOnce && previewData) {
      setValue("start_date", previewData?.start_date);
      setApplicableFor(previewData?.applicable_for ?? "Non Override Employee");
      setValue("reason", previewData?.reason);
      setTriggerOnce(false);
    }
  }, [
    previewData,
    previewData?.applicable_for,
    previewData.length,
    previewData?.reason,
    previewData?.start_date,
    setTriggerOnce,
    setValue,
    triggerOnce,
  ]);

  useEffect(() => {
    const getLastPayDate = async () => {
      try {
        let url = "";
        if (templateData?.new_template_id && revisionBasedOn === "Template") {
          url = `${domain}get-user-last-pay-date/?template_id=${currentTemplateData.id}`;
        } else {
          url = `${domain}get-user-last-pay-date/?user_id=${userId}`;
        }

        const res = await customGetAPI(url);

        if (res.ok) {
          setGetLastPayDate(res?.responseDetails?.last_pay_date);
          setOnboardDate(res?.responseDetails?.onboard_date);
          setGetDate(false);
        } else {
          showToast("error", res?.message);
        }
      } catch (error) {
        console.error(error);
      }
    };

    if (getDate) {
      getLastPayDate();
    }
  }, [
    domain,
    getDate,
    userId,
    setGetDate,
    revisionBasedOn,
    templateData?.new_template_id,
    currentTemplateData.new_template_id,
    currentTemplateData.id,
  ]);

  return (
    <Box>
      <Box>
        <Box
          sx={{
            flex: 1,
            px: 3,
            overflowY: "auto",
            border: "2px solid #f0f0f0",
          }}
        >
          <TableSection
            title="Earnings"
            data={templateData?.earnings}
            totalLabel="Total Earnings"
          />

          <TableSection
            title="Deductions"
            data={templateData?.deduction}
            totalLabel="Total Deductions"
          />
          <TableSection
            title="Employer Contribution"
            data={templateData?.employer_contribution}
            totalLabel="Total Contribution"
          />
          <TableSection
            title="Reimbursement"
            data={templateData?.reimbursement}
            totalLabel="Total Reimbursement"
          />

          <Box sx={{ display: "flex", gap: 2, my: 3 }}>
            <SummaryColumn title="Current Summary" summary={currentSummary} />
            <SummaryColumn title="New Summary" summary={newSummary} />
          </Box>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Box
            sx={{
              flex: 1,
              px: 3,
              overflowY: "auto",
              border: "2px solid #f0f0f0",
            }}
          >
            <Box mb={3}></Box>
            {revisionBasedOn !== "Employee" && (
              <Box
                mb={3}
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {/* Left text */}
                <Box sx={{ minWidth: 260 }}>
                  <Typography fontWeight={600}>Applicable for</Typography>
                  <Typography sx={infoStyle}>
                    Select the applicable employee category.
                  </Typography>
                </Box>

                {/* Center radios */}
                <Box
                  sx={{ flex: 1, display: "flex", justifyContent: "center" }}
                >
                  <RadioGroup
                    row
                    sx={{ gap: 5 }}
                    value={applicableFor}
                    onChange={(e) => setApplicableFor(e.target.value)}
                  >
                    <FormControlLabel
                      value="Non Override Employee"
                      control={<Radio />}
                      label="Non-Override Employee"
                    />
                    <FormControlLabel
                      value="Override Employee"
                      control={<Radio />}
                      label="Override Employee"
                    />
                    <FormControlLabel
                      value="Both"
                      control={<Radio />}
                      label="Both"
                    />
                  </RadioGroup>
                </Box>
              </Box>
            )}
            <Box mb={3}>
              <Typography fontWeight={600}>Effective from</Typography>
              <Typography {...infoStyle}>
                Choose the effective from date.
              </Typography>
              <CustomizedDatePicker
                name="start_date"
                control={control}
                required
                requiredMessage="Date is required"
                placeholder="Select start date"
                minDate={
                  getLastPayDate
                    ? dayjs(getLastPayDate, "DD/MM/YYYY").add(1, "day")
                    : dayjs(onboardDate, "DD/MM/YYYY").add(1, "day")
                }
              />
            </Box>

            <Box mb={3}>
              <Typography fontWeight={600}>Reason</Typography>
              <Typography {...infoStyle}>Enter reason for revision.</Typography>
              <CustomTextBox
                type="multiline"
                selectedValue={watch("reason")}
                rules={{
                  ...register("reason", {
                    required: "Reason is required",
                    validate: {
                      minLengthNoSpaces: (value) => {
                        if (value?.trim().length < 3 && value !== "") {
                          return minimum3Characters;
                        } else if (value?.trim().length > 255 && value !== "") {
                          return "Maximum length is 255 characters";
                        } else {
                          return true;
                        }
                      },
                      atleastOneCharacterWithSpaceReg: (value) => {
                        if (
                          atleastOneCharacterWithSpace.test(value) ||
                          value === ""
                        ) {
                          return true;
                        } else {
                          return "Atleast one alphabet required";
                        }
                      },
                    },
                  }),
                }}
                updateDataFunction={updateReason}
                placeHolder="Enter reason"
                errorMessage={
                  errors?.reason?.message ? errors?.reason?.message : ""
                }
                textBoxError={errors.reason ? true : false}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
